import useSWR from "swr";
import { supabaseClient } from "@/lib/supabase/client";
import type { Tables } from "@/lib/supabase/types.extended";

export type CurrentUser = Tables<"users">;

/**
 * One SWR key for the signed-in user's row, shared by the route guards and
 * `SettingsProvider`: whoever mounts first fetches, everybody else reads the
 * same cache, and a `mutate(CURRENT_USER_SWR_KEY)` refreshes both.
 */
export const CURRENT_USER_SWR_KEY = "settings-user";
/** Alias used by the action rewrites (`mutate(CURRENT_USER_KEY)`). */
export const CURRENT_USER_KEY = CURRENT_USER_SWR_KEY;

export const fetchCurrentUser = async (): Promise<CurrentUser | null> => {
  const { data: claimsData } = await supabaseClient.auth.getClaims();
  const claims = claimsData?.claims;
  if (!claims) return null;

  // `maybeSingle`: an account whose `users` row does not exist yet is a valid
  // state (first step of the onboarding), not an error.
  const { data, error } = await supabaseClient
    .from("users")
    .select("*")
    .eq("id", claims.sub)
    .maybeSingle();

  if (error) throw error;
  return data;
};

/**
 * Onboarding is over once `POST /api/onboarding/complete` dated it; the
 * organization is checked too, since leaving one sends the user back here.
 */
export const isOnboarded = (
  user:
    | Pick<CurrentUser, "onboarded_at" | "organization_id">
    | null
    | undefined,
): boolean => !!user && !!user.onboarded_at && !!user.organization_id;

export function useCurrentUser() {
  return useSWR<CurrentUser | null>(CURRENT_USER_SWR_KEY, fetchCurrentUser, {
    revalidateOnFocus: false,
  });
}
